import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const DashboardSplits = ({ allGames }) => {
  const { home, away, vsLeft, vsRight } = useMemo(() => {
    if (!allGames?.length) return { home: null, away: null, vsLeft: null, vsRight: null };
    const finished = allGames.filter(g => g.status?.abstractGameState === 'Final');
    const h = { w: 0, l: 0, rs: 0, ra: 0, g: 0 };
    const a = { w: 0, l: 0, rs: 0, ra: 0, g: 0 };

    finished.forEach(g => {
      const bosHome = g.teams?.home?.team?.id === 111;
      const bosScore = Number(bosHome ? g.teams.home.score : g.teams.away.score);
      const oppScore = Number(bosHome ? g.teams.away.score : g.teams.home.score);
      const won = bosScore > oppScore;
      const bucket = bosHome ? h : a;
      bucket.g++; bucket.rs += bosScore; bucket.ra += oppScore;
      if (won) bucket.w++; else bucket.l++;
    });

    const fmt = b => ({
      ...b,
      wpct: b.g ? ((b.w / b.g) * 100).toFixed(1) : '0.0',
      rpg: b.g ? (b.rs / b.g).toFixed(2) : '0.00',
      rd: b.rs - b.ra,
    });

    return { home: fmt(h), away: fmt(a), vsLeft: null, vsRight: null };
  }, [allGames]);

  const radarOpt = useMemo(() => {
    if (!home || !away) return null;
    return {
      backgroundColor: 'transparent',
      radar: {
        indicator: [
          { name: 'Win %', max: 100 }, { name: 'R/G', max: 8 },
          { name: 'RA/G', max: 8 }, { name: 'Wins', max: Math.max(home.w, away.w, 1) },
          { name: 'Run Diff', max: Math.max(Math.abs(home.rd), Math.abs(away.rd), 1) },
        ],
        shape: 'polygon',
        axisName: { color: '#a9c9b1', fontSize: 9, fontWeight: 'bold' },
        splitLine: { lineStyle: { color: 'rgba(101,141,113,0.3)' } },
        splitArea: { areaStyle: { color: ['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)'] } },
        axisLine: { lineStyle: { color: 'rgba(101,141,113,0.4)' } },
      },
      tooltip: { backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
      legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
      series: [{
        type: 'radar',
        data: [
          { value: [parseFloat(home.wpct), parseFloat(home.rpg), home.g ? home.ra / home.g : 0, home.w, Math.max(home.rd, 0)], name: 'Home', lineStyle: { color: '#a9c9b1', width: 2 }, areaStyle: { color: 'rgba(169,201,177,0.15)' }, itemStyle: { color: '#a9c9b1' } },
          { value: [parseFloat(away.wpct), parseFloat(away.rpg), away.g ? away.ra / away.g : 0, away.w, Math.max(away.rd, 0)], name: 'Away', lineStyle: { color: '#c4333e', width: 2 }, areaStyle: { color: 'rgba(196,51,62,0.15)' }, itemStyle: { color: '#c4333e' } },
        ],
      }],
    };
  }, [home, away]);

  if (!home) return null;

  const SplitBlock = ({ label, data, color }) => (
    <div className="border border-[#658d71] p-3 bg-[rgba(0,0,0,0.08)] flex flex-col gap-2">
      <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color }}>{label}</span>
      <div className="flex items-end gap-1">
        <span className="text-3xl font-black tabular-nums text-white">{data.w}</span>
        <span className="text-[#658d71] font-black text-xl mb-0.5">–</span>
        <span className="text-3xl font-black tabular-nums text-[#658d71]">{data.l}</span>
      </div>
      <div className="grid grid-cols-2 gap-1 text-[9px]">
        {[['Win%', `${data.wpct}%`], ['R/G', data.rpg], ['RD', data.rd > 0 ? `+${data.rd}` : data.rd], ['G', data.g]].map(([k, v]) => (
          <div key={k} className="border border-[rgba(101,141,113,0.3)] p-1 bg-[rgba(0,0,0,0.1)] flex flex-col">
            <span className="text-[#658d71] font-bold uppercase tracking-widest">{k}</span>
            <span className="text-white font-black tabular-nums">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <SplitBlock label="Home" data={home} color="#a9c9b1" />
        <SplitBlock label="Away" data={away} color="#c4333e" />
      </div>
      {radarOpt && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Home vs Away Radar</span>
          </div>
          <div className="p-2"><ReactECharts option={radarOpt} style={{ height: '200px' }} /></div>
        </div>
      )}
    </div>
  );
};

export default DashboardSplits;